import { useId } from 'react';

import styles from './LastWreckScreen.module.css';
import { Icon } from './icons';
import type { ScannerMapState } from './types';

export function ScannerMapOverlay({ map, onClose }: { map: ScannerMapState; onClose: () => void }) {
  const patternId = useId().replaceAll(':', '');
  const byId = new Map(map.rooms.map((room) => [room.id, room]));

  if (!map.visible) return null;

  return (
    <section aria-label="Scanner map" className={styles.mapOverlay}>
      <header>
        <span>
          <Icon name="map" /> WRECK SCHEMATIC
        </span>
        <small>{Math.round(map.scanProgress * 100)}% RESOLVED</small>
        <button aria-label="Close scanner map" className={styles.iconButton} onClick={onClose}>
          <Icon name="close" />
        </button>
      </header>
      <svg aria-labelledby={`${patternId}-title`} role="img" viewBox="0 0 700 390">
        <title id={`${patternId}-title`}>Eight-room wreck route map</title>
        <defs>
          <pattern height="10" id={patternId} patternUnits="userSpaceOnUse" width="10">
            <path d="M0 10 10 0" stroke="currentColor" strokeOpacity=".18" />
          </pattern>
        </defs>
        <g className={styles.mapConnections}>
          {map.connections.map((connection) => {
            const from = byId.get(connection.from);
            const to = byId.get(connection.to);
            if (from === undefined || to === undefined) return null;
            return (
              <line
                className={connection.open ? styles.routeOpen : styles.routeClosed}
                data-cuttable={connection.cuttable === true}
                key={`${connection.from}-${connection.to}`}
                x1={from.x + from.width / 2}
                x2={to.x + to.width / 2}
                y1={from.y + from.height / 2}
                y2={to.y + to.height / 2}
              />
            );
          })}
        </g>
        {map.rooms.map((room, index) => (
          <g className={styles.mapRoom} data-state={room.state} key={room.id}>
            <rect height={room.height} rx="5" width={room.width} x={room.x} y={room.y} />
            {room.state === 'unknown' && (
              <rect
                className={styles.unknownPattern}
                fill={`url(#${patternId})`}
                height={room.height}
                rx="5"
                width={room.width}
                x={room.x}
                y={room.y}
              />
            )}
            <text className={styles.roomNumber} x={room.x + 10} y={room.y + 20}>
              {String(index + 1).padStart(2, '0')}
            </text>
            <text className={styles.roomLabel} x={room.x + 10} y={room.y + room.height - 12}>
              {room.state === 'unknown' ? 'UNRESOLVED' : room.label.toUpperCase()}
            </text>
            {(room.salvageCount ?? 0) > 0 && room.state !== 'unknown' && (
              <g className={styles.salvageMark}>
                <circle cx={room.x + room.width - 15} cy={room.y + 15} r="7" />
                <text x={room.x + room.width - 15} y={room.y + 18}>
                  {room.salvageCount}
                </text>
              </g>
            )}
          </g>
        ))}
      </svg>
      <ul className={styles.srOnly}>
        {map.rooms.map((room) => (
          <li key={`${room.id}-accessible`}>
            {room.label}: {room.state}
            {(room.salvageCount ?? 0) > 0
              ? `, ${String(room.salvageCount)} salvage marker${room.salvageCount === 1 ? '' : 's'}`
              : ''}
          </li>
        ))}
      </ul>
      <footer>
        <span>
          <i data-kind="current" /> Current room
        </span>
        <span>
          <i data-kind="powered" /> Powered route
        </span>
        <span>
          <i data-kind="hazard" /> Structural risk
        </span>
        <span>
          <i data-kind="cut" /> Cut passage
        </span>
      </footer>
    </section>
  );
}
